import React from 'react';
import { LaunchIcon, GoalIcon, TipIcon } from '../../../icons';

interface ExecutionReadyCardProps {
  goalTitle: string;
  totalMilestones: number;
  totalActions: number;
  onClose: () => void;
}

/**
 * ExecutionReadyCard - Celebratie kaart wanneer alle voorbereidingen zijn afgerond
 * 
 * Verschijnt automatisch wanneer:
 * - Alle acties zijn afgevinkt
 * - De fase-indicator naar 'execution' gaat
 * 
 * Geeft de gebruiker een duidelijk moment van "Je kunt nu van start!"
 */
const ExecutionReadyCard: React.FC<ExecutionReadyCardProps> = ({
  goalTitle,
  totalMilestones,
  totalActions,
  onClose,
}) => {
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      style={{ animation: 'fadeIn 0.3s ease-out' }}
    >
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes scaleIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        @keyframes rocket {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>
      <div 
        className="bg-gradient-to-br from-orange-50 via-blue-50 to-green-50 border-2 border-orange-400 rounded-xl p-8 shadow-2xl max-w-lg w-full"
        style={{ animation: 'scaleIn 0.3s ease-out' }}
      >
        {/* Rocket icon with animation */}
        <div className="flex justify-center mb-6">
          <div 
            className="w-24 h-24 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg"
            style={{ animation: 'rocket 2s ease-in-out infinite' }}
          >
            <LaunchIcon size={48} />
          </div>
        </div>

        {/* Success message */}
        <div className="text-center mb-6">
          <h3 className="text-3xl font-bold text-gray-900 mb-3">
            Klaar voor de start!
          </h3>
          <p className="text-lg text-gray-700 mb-4 leading-relaxed">
            Je hebt alle voorbereidingen voor <strong>"{goalTitle}"</strong> afgerond
          </p>

          {/* Checklist van wat er is bereikt */}
          <div className="bg-white rounded-lg p-4 mb-4 border border-green-200 text-left">
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex items-center gap-2">
                <span className="text-green-500 font-bold">✓</span>
                <span>Doel gedefinieerd en commitment vastgelegd</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500 font-bold">✓</span>
                <span>Skills in kaart gebracht</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500 font-bold">✓</span>
                <span>Sparring sessie voltooid</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500 font-bold">✓</span>
                <span>{totalMilestones} mijlpa{totalMilestones === 1 ? 'al' : 'len'} vastgelegd</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500 font-bold">✓</span>
                <span>Alle {totalActions} acti{totalActions === 1 ? 'e' : 'es'} afgevinkt</span>
              </div>
            </div>
          </div>

          {/* Call to action message */}
          <div className="bg-gradient-to-r from-orange-100 to-blue-100 rounded-lg p-4 mb-4 border-2 border-orange-300">
            <p className="text-lg font-bold text-gray-900 mb-2">
              <GoalIcon size={16} className="inline" /> Je kunt nu van start met het uitvoeren van je plan!
            </p>
            <p className="text-sm text-gray-700">
              Gebruik je agenda en focus-overzicht om wekelijks/maandelijks je voortgang bij te houden
            </p>
          </div>
        </div>

        {/* Action button */}
        <button
          onClick={onClose}
          className="w-full py-4 px-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-lg font-bold hover:from-orange-600 hover:to-orange-700 transition-all shadow-md hover:shadow-lg"
        >
          <span className="flex items-center justify-center gap-2">
            <span>Aan de slag!</span>
            <span>→</span>
          </span>
        </button>

        {/* Tip */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800 text-center">
            <TipIcon size={14} className="inline" /> <strong>Tip:</strong> Je kunt altijd nieuwe acties toevoegen of je plan aanpassen via de Journey pagina
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExecutionReadyCard;
